import { useId } from "react";
import type { DeliveryDateOption } from "@shared-core/domain/delivery-dates";
import {
  RadioGroup,
  RadioGroupTileItem,
} from "../../../app/components/primitives/radio-group";

// When the order arrives, chosen on the item page as a date with what it costs over the cheapest way (Brad,
// 22 Oct: the customer sees dates, never the Speed's name). The dates and prices are the server's; the words
// around them are shared-core's (`deliveryDateOptions`, from the item page's Speeds, or `deliveryDateOptionsOf`, from a
// basket line's preview: the same choice on the item page and in Change delivery). One option is no choice: it is said as one line, like
// the arrival line used to be. More than one is a radiogroup in the server's order (cheapest first), the
// selected one being the one the price above is at. Nothing here names a Speed.
export function DeliveryDateChoice({
  options,
  onSelect,
  disabled = false,
}: {
  options: DeliveryDateOption[];
  onSelect: (speedCode: string) => void;
  disabled?: boolean;
}) {
  const labelId = useId();
  if (options.length === 0) return null;

  const only = options[0];
  if (options.length === 1 && only) {
    return (
      <p className="text-sm text-muted-foreground">
        {only.isToBeConfirmed
          ? only.when
          : `${only.isEstimate ? "Usually arrives" : only.isWindow ? "Expected" : "Arrives"} ${only.when}`}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium" id={labelId}>
        Delivery date
      </label>
      <RadioGroup
        value={options.find((o) => o.isSelected)?.speedCode ?? ""}
        onValueChange={onSelect}
        aria-labelledby={labelId}
        disabled={disabled}
        className="grid-cols-[minmax(0,1fr)] gap-1.5"
      >
        {options.map((option) => (
          <RadioGroupTileItem
            key={option.speedCode}
            value={option.speedCode}
            className="flex min-h-9 w-full min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-0 rounded-lg px-3 py-1 text-left font-normal"
          >
            <span className="flex items-baseline gap-x-2">
              <span className="font-semibold">{option.when}</span>
              {option.isEstimate && (
                <span className="text-xs text-muted-foreground">usually</span>
              )}
            </span>
            <span className="text-sm text-muted-foreground tabular-nums">
              {option.extraLabel}
            </span>
          </RadioGroupTileItem>
        ))}
      </RadioGroup>
    </div>
  );
}
